import { useRef } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Feat } from '../types';
import { PRINT_MODE_STYLES } from '../constants/print';
import { useAutoScaleFont } from '../hooks/useAutoScaleFont';
import './FeatCardExpanded.css';

interface FeatCardExpandedProps {
  feat: Feat;
  isPrintMode?: boolean;
  borderColor?: string;
}

export function FeatCardExpanded({ feat, isPrintMode, borderColor }: FeatCardExpandedProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);

  const fontSize = useAutoScaleFont({
    cardRef,
    descriptionRef,
    entityId: feat.id,
    entityType: 'feat',
    deps: [feat.description, feat.name, isPrintMode],
  });

  const dynamicStyles: React.CSSProperties = {};
  if (borderColor) {
    dynamicStyles.border = `4px solid ${borderColor}`;
  }

  const printStyles: React.CSSProperties | undefined = isPrintMode
    ? { ...PRINT_MODE_STYLES, ...(borderColor ? { border: `4px solid ${borderColor}` } : {}) }
    : undefined;

  return (
    <div
      ref={cardRef}
      className={`feat-card-expanded ${isPrintMode ? 'print-mode' : ''}`}
      style={printStyles ?? (borderColor ? dynamicStyles : undefined)}
    >
      <div className="card-header">
        <h2>{feat.name}</h2>
      </div>

      {feat.prerequisite && (
        <div className="card-meta">
          <span className="feat-card-prereq-label">Prerequisite: {feat.prerequisite}</span>
        </div>
      )}

      <div
        ref={descriptionRef}
        className="card-description"
        style={{ '--scale-factor': fontSize / 100 } as React.CSSProperties}
      >
        <Markdown remarkPlugins={[remarkGfm]}>{feat.description}</Markdown>
      </div>

      <div className="card-footer">
        <span className="feat-card-category">{feat.category} Feat</span>
      </div>
    </div>
  );
}
